import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, GitCompare, MessageSquareText } from "lucide-react";
import { siteConfig, fmtMoney, fmtMiles } from "../config";
import { savedStore, compareStore } from "../lib/storage";
import { buildPriceStack, publicSavingsBadgeAmount } from "../lib/pricing";

export default function VehicleCard({ vehicle, priority }) {
  const v = vehicle;
  const [saved, setSaved] = useState(false);
  const [compare, setCompare] = useState(false);
  const [limitMsg, setLimitMsg] = useState(false);

  useEffect(() => {
    setSaved(savedStore.has(v.slug));
    setCompare(compareStore.has(v.slug));
    const onChange = () => {
      setSaved(savedStore.has(v.slug));
      setCompare(compareStore.has(v.slug));
    };
    window.addEventListener("ssc-storage", onChange);
    return () => window.removeEventListener("ssc-storage", onChange);
  }, [v.slug]);

  const photo = (v.photos && v.photos[0]) || null;
  const detailHref = `/${v.status}-vehicles/${v.slug}`;
  const title = `${v.year} ${v.make} ${v.model} ${v.trim || ""}`.trim();
  const features = (v.options || []).slice(0, 3);
  const askText = `Hi Scott, I have a question about Stock ${v.stock_number} — ${title}.`;
  const smsHref = `${siteConfig.scott.phoneSms}?&body=${encodeURIComponent(askText)}`;

  const onCompareClick = () => {
    if (compare) return; // primary action; secondary link handles view
    const res = compareStore.add(v);
    if (!res.ok && res.reason === "limit") {
      setLimitMsg(true);
      setTimeout(() => setLimitMsg(false), 2500);
      return;
    }
    setCompare(true);
  };

  return (
    <article className="ssc-card flex flex-col" data-testid={`vehicle-card-${v.stock_number}`}>
      <Link to={detailHref} className="block">
        <div className="ssc-vimg">
          {photo ? (
            <img
              src={photo.url}
              alt={photo.alt || title}
              loading={priority ? "eager" : "lazy"}
              width="640" height="400"
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-[var(--ssc-ink-2)] text-sm p-4 text-center">
              Photo coming soon. Text Scott for details.
            </div>
          )}
          <span className={`absolute top-3 left-3 ssc-badge ${v.status === "new" ? "ssc-badge-new" : "ssc-badge-used"}`}>
            {v.status === "new" ? "New" : "Used"}
          </span>
          {v.is_special && (
            <span className="absolute top-3 right-3 ssc-badge ssc-badge-special">Special</span>
          )}
        </div>
      </Link>

      <div className="mt-4 flex-1 flex flex-col">
        <Link to={detailHref} className="font-extrabold text-lg leading-tight text-[var(--ssc-navy)] hover:underline">
          {title}
        </Link>
        <div className="text-sm text-[var(--ssc-ink-2)] mt-1">Stock #{v.stock_number}</div>

        <div className="mt-2">
          {/* New-vehicle compact pricing stack — uses shared lib/pricing logic.
              Public Bonus/Customer Cash is itemized only when it reconciles
              (CASE 1). Otherwise it surfaces as a green "Additional Public
              Savings" badge below the price (CASE 2). vAuto price is the
              source of truth for the Price row in every case. */}
          {v.status === "new" && (() => {
            const stack = buildPriceStack(v);
            if (stack.mode === "simple") {
              return (
                <div className="flex items-baseline gap-3">
                  <div className="text-2xl font-extrabold text-[var(--ssc-navy)] tabular-nums" data-testid={`card-price-${v.stock_number}`}>{fmtMoney(v.price)}</div>
                </div>
              );
            }
            const additional = publicSavingsBadgeAmount(stack);
            return (
              <div data-testid={`card-pricing-${v.stock_number}`}>
                {stack.msrp && (
                  <div className="flex items-baseline justify-between text-xs text-[var(--ssc-ink-2)]">
                    <span>MSRP</span>
                    <span className="line-through tabular-nums" data-testid={`card-msrp-${v.stock_number}`}>{fmtMoney(stack.msrp)}</span>
                  </div>
                )}
                {stack.dealerDiscount > 0 && (
                  <div className="flex items-baseline justify-between text-xs text-[var(--ssc-ink-2)]">
                    <span>Dealer Discount</span>
                    <span className="tabular-nums text-red-700 font-bold" data-testid={`card-discount-${v.stock_number}`}>
                      −{fmtMoney(stack.dealerDiscount)}
                    </span>
                  </div>
                )}
                {stack.publicInlineOffers.map((o, i) => (
                  <div key={`pub-${i}`} className="flex items-baseline justify-between text-xs text-emerald-700">
                    <span className="truncate pr-2">{o.name}</span>
                    <span className="tabular-nums font-bold" data-testid={`card-public-offer-${v.stock_number}-${i}`}>
                      −{fmtMoney(o.amount)}
                    </span>
                  </div>
                ))}
                <div className="flex items-baseline justify-between mt-0.5">
                  <span className="text-xs text-[var(--ssc-blue)] font-extrabold uppercase tracking-wider">Price</span>
                  <span className="text-2xl font-extrabold text-[var(--ssc-navy)] tabular-nums" data-testid={`card-price-${v.stock_number}`}>{fmtMoney(stack.vautoPrice)}</span>
                </div>
                {(additional > 0 || stack.additionalPublicOffers.length > 0 || v.byers_pdf_special_financing?.length > 0) && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`card-offers-badges-${v.stock_number}`}>
                    {additional > 0 && (
                      <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-blue-50 text-[var(--ssc-blue)] border border-[var(--ssc-blue)]/30" data-testid={`card-additional-savings-${v.stock_number}`}>
                        Public rebates available
                      </span>
                    )}
                    {v.byers_pdf_special_financing?.length > 0 && (
                      <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        Special APR available
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {v.status === "used" && (
            <div className="flex items-baseline gap-3">
              <div className="text-2xl font-extrabold text-[var(--ssc-navy)] tabular-nums" data-testid={`card-price-${v.stock_number}`}>{fmtMoney(v.price)}</div>
              {v.mileage != null && (
                <div className="text-sm text-[var(--ssc-ink-2)]">{fmtMiles(v.mileage)}</div>
              )}
            </div>
          )}
        </div>

        {features.length > 0 && (
          <ul className="mt-3 text-sm text-[var(--ssc-ink-2)] space-y-1">
            {features.map((f, i) => (
              <li key={i} className="flex gap-2"><span className="text-[var(--ssc-blue)]">•</span><span>{f}</span></li>
            ))}
          </ul>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link to={detailHref} className="ssc-btn ssc-btn-navy" data-testid={`view-details-${v.stock_number}`}>
            View Details
          </Link>
          <a href={smsHref} className="ssc-btn ssc-btn-primary" data-testid={`text-scott-${v.stock_number}`}>
            <MessageSquareText size={16} /> Text Scott
          </a>
          <button
            type="button"
            className={`ssc-btn ${saved ? "ssc-btn-red" : "ssc-btn-outline"}`}
            onClick={() => { savedStore.toggle(v); setSaved(!saved); }}
            data-testid={`save-${v.stock_number}`}
          >
            <Heart size={16} /> {saved ? "Saved" : "Save"}
          </button>
          <button
            type="button"
            className={`ssc-btn ${compare ? "ssc-btn-navy" : "ssc-btn-outline"}`}
            onClick={onCompareClick}
            data-testid={`compare-${v.stock_number}`}
          >
            <GitCompare size={16} /> {compare ? "Added" : "Compare"}
          </button>
        </div>
        {compare && (
          <Link
            to="/compare"
            className="mt-2 inline-block text-xs font-extrabold text-[var(--ssc-blue)] hover:underline"
            data-testid={`view-compare-${v.stock_number}`}
          >
            View Compare →
          </Link>
        )}
        {limitMsg && (
          <div
            className="mt-2 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5"
            data-testid={`compare-limit-${v.stock_number}`}
          >
            You can compare up to 6 vehicles. Remove one to add another.
          </div>
        )}
      </div>
    </article>
  );
}
